import React, { useMemo } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import actions from '@src/redux/actions';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { updateProject } from '@src/apis/project';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import usePaywallTracking from '@src/hooks/usePaywallTracking';
import {
  MOE_MODULE,
  MOE_PAYWALL_TRIGGER,
  MOE_PAYWALL_TYPE,
} from '@src/constants/moengage';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import {
  CONVERTING_STATUS,
  MAX_TOTAL_CHARACTERS,
  PROJECT_PRODUCT,
  TRANSLATION_STATUS,
} from '@src/constants/project';
import {
  buildProjectPayload,
  buildVideoSettingsPayload,
} from '@src/services/project';
import NotificationDialogV4 from '@src/components/NotificationDialogV4';
import WaringImg from '@src/assets/images/warning.png';
import { delimitNumber } from '@src/utils/number';
import { countTextLength } from '@src/services/tts';

const SaveButton = () => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const dispatch = useDispatch();
  const { getFeatureValue } = useFeatureFlags();

  const {
    multiBlock,
    title,
    voiceDefault,
    audioType,
    isProjectSynthesizing,
    product,
    translation,
    videoSettings,
    isGeneratingNarration,
  } = useSelector((state) => state.project);
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  const projectId = location.pathname.split('/')[2];
  const { trackHit: trackPaywall } = usePaywallTracking({
    module: MOE_MODULE.PROJECT,
    product,
    projectId,
    trigger: MOE_PAYWALL_TRIGGER.SAVE,
  });
  const [isSaving, setIsSaving] = React.useState(false);
  const [openMaxCharsLimitExceeded, setOpenMaxCharsLimitExceeded] =
    React.useState(false);
  const videoDubbingAspectRatioEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_ASPECT_RATIO,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );
  const videoDubbingBgColorEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_BG_COLOR,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const { blocks } = multiBlock || {};

  const isTranslationSuccess =
    product === PROJECT_PRODUCT.DUBBING &&
    translation?.status === TRANSLATION_STATUS.SUCCESS;

  const totalCharactersWithoutCreditFactor = useMemo(
    () =>
      Object.values(blocks || {}).reduce((total, block) => {
        const text = isTranslationSuccess
          ? block?.translatedText || ''
          : block?.text || '';
        if (!text) return total;
        return total + countTextLength(text);
      }, 0),
    [blocks, isTranslationSuccess],
  );

  const isMultiBlockProcessing = useMemo(
    () =>
      Object.values(blocks).filter(
        (block) => block.status === CONVERTING_STATUS.IN_PROGRESS,
      ).length > 0,
    [blocks],
  );
  const isDubbingProjectSythesizing =
    product === PROJECT_PRODUCT.DUBBING && isProjectSynthesizing;
  const isPresentationGeneratingNarration =
    product === PROJECT_PRODUCT.PRESENTATION && isGeneratingNarration;
  const saveProject = async () => {
    if (isPresentationGeneratingNarration) return null;
    try {
      setIsSaving(true);
      const videoSettingsPayload = buildVideoSettingsPayload({
        aspectRatioEnabled: videoDubbingAspectRatioEnabled,
        backgroundColorEnabled: videoDubbingBgColorEnabled,
        videoSettings,
      });
      const response = await updateProject(
        projectId,
        buildProjectPayload({
          title,
          blockIds: multiBlock?.blockIds || [],
          blocks,
          voiceDefault,
          audioType,
          isPresentation: product === PROJECT_PRODUCT.PRESENTATION,
          videoSettings:
            product === PROJECT_PRODUCT.DUBBING
              ? videoSettingsPayload
              : undefined,
        }),
      );

      setIsSaving(false);

      if (response?.status) {
        dispatch(actions.project.saveProject());
        dispatch(
          actions.noti.push({
            severity: 'success',
            message: t('saveProjectSuccessful'),
          }),
        );
        return response;
      }

      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'errorOccurred',
        }),
      );
      return null;
    } catch (error) {
      setIsSaving(false);
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'errorOccurred',
        }),
      );
      return null;
    }
  };

  const hasOneBlockEmpty =
    Object.values(blocks).length === 1 &&
    Object.values(blocks).filter((block) => !block.text);

  const isDisabledBtnSave =
    hasOneBlockEmpty ||
    isMultiBlockProcessing ||
    isDubbingProjectSythesizing ||
    isPresentationGeneratingNarration;

  const handleClickSave = () => {
    if (isDisabledBtnSave) return;
    if (totalCharactersWithoutCreditFactor > MAX_TOTAL_CHARACTERS) {
      trackPaywall(MOE_PAYWALL_TYPE.MAX_CHARS, {
        characters: totalCharactersWithoutCreditFactor,
        maxCharacters: MAX_TOTAL_CHARACTERS,
      });
      setOpenMaxCharsLimitExceeded(true);
      return;
    }
    saveProject();
  };

  const handleCloseMaxCharsLimitExceededDialog = () =>
    setOpenMaxCharsLimitExceeded(false);

  return (
    <>
      <Button
        startIcon={
          isSaving ? (
            <CircularProgress
              value={75}
              className="circular-progress"
              size="24px"
            />
          ) : (
            <SaveIcon style={{ fontSize: '24px' }} />
          )
        }
        className="save-btn"
        variant="outlined"
        disabled={isDisabledBtnSave}
        onClick={handleClickSave}
      >
        {t('saveName')}
      </Button>
      {openMaxCharsLimitExceeded && (
        <NotificationDialogV4
          open
          onClose={handleCloseMaxCharsLimitExceededDialog}
          image={WaringImg}
          title={t('maxCharsLimitExceeded')}
          description={t('pronunciationCheckMaxCharsDesc', {
            maxLengthInputText: delimitNumber(MAX_TOTAL_CHARACTERS, language),
          })}
          actionComponent={
            <Button
              variant="contained"
              onClick={handleCloseMaxCharsLimitExceededDialog}
            >
              {t('understood')}
            </Button>
          }
        />
      )}
    </>
  );
};

export default SaveButton;
